// App configuration DAO — read/write access to app_configuration table.
// Wraps raw Supabase queries so callers never touch the table directly.

import { BaseDao, type SupabaseClient } from '../base.ts';

// ── Types ────────────────────────────────────────────────────────────────

export interface AppConfigRow {
  key: string;
  value: unknown;
  description: string | null;
  created_at: string;
  updated_at: string;
}

// ── AppConfigDao ─────────────────────────────────────────────────────────

export class AppConfigDao extends BaseDao<AppConfigRow> {
  protected readonly dependentResources = ['app_configuration'];

  constructor(sb: SupabaseClient) {
    super(sb, 'app_configuration');
  }

  /**
   * Fetch a set of keys and return a map of key → raw value (or null if absent).
   * Uses .in() so all keys are fetched in one round-trip.
   */
  async getKeys(keys: string[]): Promise<Record<string, unknown | null>> {
    if (keys.length === 0) return {};
    const { data, error } = await this.sb
      .from('app_configuration')
      .select('key, value')
      .in('key', keys);
    if (error) throw error;
    const map: Record<string, unknown | null> = Object.fromEntries(
      keys.map((k) => [k, null]),
    );
    for (const row of data ?? []) {
      map[(row as AppConfigRow).key] = (row as AppConfigRow).value;
    }
    return map;
  }

  /**
   * Fetch a single key's value. Returns null if the row is absent.
   * The value is JSONB — callers must cast / parse as appropriate.
   */
  async getKey(key: string): Promise<unknown | null> {
    const { data, error } = await this.sb
      .from('app_configuration')
      .select('value')
      .eq('key', key)
      .maybeSingle();
    if (error) throw error;
    return data ? (data as { value: unknown }).value : null;
  }

  /**
   * Update the value (and optionally description) of an existing key.
   * Throws if the row does not exist (upsert is not supported to avoid
   * silent schema drift — all keys must be seeded via migrations).
   */
  async setKey(key: string, value: unknown, description?: string): Promise<void> {
    const patch: Record<string, unknown> = { value };
    if (description !== undefined) patch['description'] = description;
    const { error } = await this.sb
      .from('app_configuration')
      .update(patch)
      .eq('key', key);
    if (error) throw error;
  }

  /**
   * Insert a new key. Throws on conflict.
   */
  async insertKey(key: string, value: unknown, description?: string): Promise<void> {
    const { error } = await this.sb
      .from('app_configuration')
      .insert({ key, value, description: description ?? null });
    if (error) throw error;
  }

  /** Return all rows ordered by key (used by the admin settings page). */
  async listAll(): Promise<AppConfigRow[]> {
    const { data, error } = await this.sb
      .from('app_configuration')
      .select('*')
      .order('key', { ascending: true });
    if (error) throw error;
    return (data ?? []) as AppConfigRow[];
  }
}
